import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  FileText, Receipt, PenLine, ArrowLeft, Loader2, Sparkles, X, Search,
  CheckCircle2, ChevronRight, Building, Hash,
} from 'lucide-react';

/**
 * Modal for creating a new facture — three paths :
 *  1. From an existing signed devis (most common)
 *  2. Manual creation (blank facture, fill on the next page)
 *  3. AI-assisted (re-uses the DevisGenerator pattern in a later iteration)
 */
export default function NewFactureDialog({ open, onOpenChange, onFactureCreated }) {
  const navigate = useNavigate();
  const [mode, setMode] = useState(null);          // null | 'from-devis' | 'manual'
  const [creating, setCreating] = useState(false);
  const [devisList, setDevisList] = useState([]);
  const [loadingDevis, setLoadingDevis] = useState(false);
  const [search, setSearch] = useState('');
  const [bcByDevis, setBcByDevis] = useState({});  // {devis_id: bc_value}

  const reset = () => {
    setMode(null);
    setCreating(false);
    setSearch('');
    setBcByDevis({});
  };

  const close = () => {
    reset();
    onOpenChange(false);
  };

  useEffect(() => {
    if (mode === 'from-devis' && devisList.length === 0) {
      setLoadingDevis(true);
      api.get('/devis').then((r) => {
        // Exclude devis already linked to a facture
        const all = (r.data || []).filter((d) => !d.facture_id);
        setDevisList(all);
      }).catch(() => toast.error("Impossible de charger les devis"))
        .finally(() => setLoadingDevis(false));
    }
  }, [mode, devisList.length]);

  const convertFromDevis = async (devisId) => {
    const bc = (bcByDevis[devisId] || '').trim();
    if (!bc) {
      toast.error("Saisissez le n° du bon de commande avant de convertir");
      return;
    }
    setCreating(true);
    try {
      const r = await api.post(`/devis/${devisId}/convert-to-facture`, {
        numero_bon_commande: bc,
        identique_au_devis: true,
        delai_paiement_jours: 30,
      });
      close();
      navigate(`/factures/${r.data.facture.facture_id}`);
      toast.success(`Facture ${r.data.facture.numero} créée`);
      if (onFactureCreated) onFactureCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la conversion');
      setCreating(false);
    }
  };

  const startManual = async () => {
    setCreating(true);
    try {
      const r = await api.post('/factures', {
        numero_bon_commande: '',
        client_info: {},
        lignes: [],
        delai_paiement_jours: 30,
      });
      close();
      navigate(`/factures/${r.data.facture.facture_id}`);
      toast.success('Facture créée — complétez les informations');
      if (onFactureCreated) onFactureCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la création');
      setCreating(false);
    }
  };

  const filteredDevis = devisList.filter((d) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (d.devis_id || '').toLowerCase().includes(q) ||
      (d.client_info?.nom || '').toLowerCase().includes(q) ||
      (d.project_title || '').toLowerCase().includes(q)
    );
  });

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto p-0">
        <div className="sticky top-0 bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            {mode && (
              <button
                onClick={reset}
                className="p-1.5 -ml-1 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-lg"
                data-testid="new-facture-back"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
            )}
            <div>
              <h2 className="text-lg font-bold text-slate-900">
                {!mode && 'Nouvelle facture'}
                {mode === 'from-devis' && 'Convertir un devis en facture'}
                {mode === 'manual' && 'Création manuelle'}
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">
                {!mode && 'Choisissez la méthode de création'}
                {mode === 'from-devis' && 'Sélectionnez le devis à facturer et saisissez le n° du BC'}
                {mode === 'manual' && 'Création directe sans devis source'}
              </p>
            </div>
          </div>
          <button
            onClick={close}
            className="p-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-lg"
            data-testid="new-facture-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          {/* CHOOSER */}
          {!mode && (
            <div key="chooser" className="space-y-4" data-testid="new-facture-chooser">
              <p className="text-sm text-slate-600">
                <Sparkles className="w-4 h-4 inline mr-1 text-blue-500" />
                Deux manières de créer une facture :
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Tile 1 — From devis */}
                <button
                  onClick={() => setMode('from-devis')}
                  className="group relative p-5 rounded-xl border-2 border-slate-200 hover:border-blue-400 hover:shadow-lg transition-all text-left bg-white"
                  data-testid="tile-from-devis"
                >
                  <div className="w-12 h-12 rounded-xl bg-blue-100 group-hover:bg-blue-200 flex items-center justify-center mb-3 transition-colors">
                    <FileText className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="font-bold text-slate-900 mb-1">Depuis un devis signé</h3>
                  <p className="text-xs text-blue-600 font-medium mb-2">Recommandé</p>
                  <p className="text-sm text-slate-600 leading-snug">
                    Choisissez le devis et saisissez le n° du bon de commande.
                    Les lignes, le client et le chantier sont repris automatiquement.
                  </p>
                </button>

                {/* Tile 2 — Manual */}
                <button
                  onClick={startManual}
                  disabled={creating}
                  className="group relative p-5 rounded-xl border-2 border-slate-200 hover:border-emerald-400 hover:shadow-lg transition-all text-left bg-white disabled:opacity-60"
                  data-testid="tile-manual-facture"
                >
                  <div className="w-12 h-12 rounded-xl bg-emerald-100 group-hover:bg-emerald-200 flex items-center justify-center mb-3 transition-colors">
                    {creating
                      ? <Loader2 className="w-6 h-6 text-emerald-600 animate-spin" />
                      : <PenLine className="w-6 h-6 text-emerald-600" />}
                  </div>
                  <h3 className="font-bold text-slate-900 mb-1">Création manuelle</h3>
                  <p className="text-xs text-emerald-600 font-medium mb-2">Sans devis source</p>
                  <p className="text-sm text-slate-600 leading-snug">
                    Crée une facture vierge. Vous saisissez ensuite le client,
                    le n° BC et les lignes sur la fiche de la facture.
                  </p>
                </button>
              </div>

              <div className="mt-4 p-3 bg-slate-50 rounded-lg text-xs text-slate-500">
                <b>Astuce</b> — Conformément à la loi française, vous devez conserver le lien entre le devis signé
                et la facture émise pour les marchés publics.
              </div>
            </div>
          )}

          {/* MODE: FROM DEVIS */}
          {mode === 'from-devis' && (
            <div key="from-devis" className="space-y-3" data-testid="from-devis-list">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Rechercher (n° devis, client, projet)..."
                  className="pl-9"
                />
              </div>

              {loadingDevis ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                </div>
              ) : filteredDevis.length === 0 ? (
                <div className="p-8 text-center text-slate-400 text-sm">
                  {search ? 'Aucun devis ne correspond à votre recherche' : 'Aucun devis disponible à facturer (tous les devis sont déjà liés à une facture)'}
                </div>
              ) : (
                <div className="space-y-2 max-h-[440px] overflow-y-auto">
                  {filteredDevis.map((d) => (
                    <div
                      key={d.devis_id}
                      className="p-4 bg-white border border-slate-200 rounded-xl hover:border-blue-300 transition-colors"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                          <FileText className="w-4 h-4 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-slate-900">{d.devis_id}</p>
                          <p className="text-xs text-slate-500 mt-0.5 truncate">
                            <Building className="w-3 h-3 inline mr-1" />{d.client_info?.nom || '—'}
                            {d.project_title && <span className="ml-1.5">· {d.project_title}</span>}
                          </p>
                          <div className="flex items-center gap-2 mt-2">
                            <div className="relative flex-1">
                              <Hash className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" />
                              <Input
                                value={bcByDevis[d.devis_id] || ''}
                                onChange={(e) => setBcByDevis((s) => ({ ...s, [d.devis_id]: e.target.value }))}
                                placeholder="N° du bon de commande"
                                className="h-8 pl-7 text-sm"
                                data-testid={`bc-input-${d.devis_id}`}
                              />
                            </div>
                            <Button
                              size="sm"
                              onClick={() => convertFromDevis(d.devis_id)}
                              disabled={creating || !(bcByDevis[d.devis_id] || '').trim()}
                              className="bg-emerald-600 hover:bg-emerald-700 h-8"
                              data-testid={`convert-${d.devis_id}`}
                            >
                              {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />}
                              Convertir
                            </Button>
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-sm font-bold text-slate-900">
                            {(d.montant_total || d.totaux?.total_ttc || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                          </p>
                          <p className="text-[10px] text-slate-400">TTC</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
